<template>
  <div>
    <loading v-if="loading" />

    <div class="space-y-4">
      <base-card title="Faktur">
        <template #action>
          <base-badge :color="valid ? 'green' : 'yellow'">{{
            valid ? 'Valid' : 'Draft'
          }}</base-badge>
        </template>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <base-input
            v-if="withReferenceCode"
            type="text"
            inset
            required
            :label="`No. ${type}`"
            :placeholder="`Masukkan No. ${type}`"
            v-model="order.referenceCode"
            @change="onChangeReferenceCode"
          >
            <template v-if="order.referenceCodeSaved" #next-input>
              <Icon icon="heroicons:check-circle-solid" class="text-green-500 absolute right-3 w-5 h-5" style="top: 24px" />
            </template>
          </base-input>
          <div :class="withReferenceCode ? '' : 'sm:col-span-2'">
            <base-input
              inset
              :disabled="disabledDate"
              label="Tanggal"
              type="date"
              force-left
              :required="dateMismatch"
              v-model="order.date"
              @change="onChangeDate"
            />
          </div>
          <div v-if="withOriginOfficeType" class="col-span-2">
            <base-input inset label="Pilih Kantor Tujuan">
              <base-select
                inset
                expand
                :options="[
                  { key: 'none', value: null, label: 'Pilih Kantor Tujuan' },
                  ...originOfficeTypeOptions,
                ]"
                v-model="order.originOfficeType"
                @change="onChangeOriginOfficeType"
              />
            </base-input>
          </div>
          <template v-if="!withOriginOfficeType || order.originOfficeType">
            <template v-if="withOriginOffice">
              <search-office-input
                :disabled="originOfficeDisabled || saved"
                :office-type="order.originOfficeType"
                with-current-warehouse
                :with-area="withOriginOfficeArea"
                :with-check-order-origin="order.destinationOfficeType === 'stockist' && order.destinationOfficeType !== 'pusat'"
                :check-destination-office-id="order.destinationOffice?.id"
                v-model="order.originOffice"
                @change="onChangeOriginOffice"
              />
              <base-input
                inset
                disabled
                :label="`Nama ${originOfficeLabel}`"
                :id="`nama_${order.originOfficeType}`"
                placeholder="HMI"
                :value="order.originOffice.name"
              />
            </template>
            <template v-if="withDestinationOffice">
              <div
                :class="[
                  'sm:col-span-2 grid gap-4',
                  withStockistCenterBalance ? 'grid-cols-8' : 'grid-cols-6',
                ]"
              >
                <search-office-input
                  :disabled="
                    saved ||
                    forceDestination ||
                    (withReferenceCode && !order.referenceCode) ||
                    !order.date
                  "
                  with-address
                  :class="[
                    withStockistCenterBalance || withOrderBalance
                      ? 'col-span-2'
                      : 'col-span-full sm:col-span-3',
                  ]"
                  :with-area="withDestinationArea"
                  :with-stockist-center-balance="withStockistCenterBalance"
                  :with-order-balance="withOrderBalance"
                  :office-type="order.destinationOfficeType"
                  :with-upline="withUpline"
                  :with-phone="withAddressReceipt"
                  :use-custom-address="useCustomAddress"
                  :use-backup-upline-address="useBackupUplineAddress"
                  :with-upline-histories="withOriginStockist"
                  :table-columns="order.originOfficeType === 'stockist_center' ? [] : ['address', 'phone']"
                  :filter-active="order.destinationOfficeType === 'member'"
                  with-current-warehouse
                  :with-check-order-destination="order.destinationOfficeType === 'stockist'"
                  :check-origin-office-id="useOriginOfficeWarehouse ? order.originOffice.id :order.originWarehouse.officeId"
                  v-model="order.destinationOffice"
                  @change="onChangeDestinationOffice"
                />
                <base-input
                  inset
                  disabled
                  :class="[
                    withStockistCenterBalance || withOrderBalance
                      ? 'col-span-2'
                      : 'col-span-full sm:col-span-3',
                  ]"
                  :label="`Nama ${destinationOfficeLabel}`"
                  :id="`nama_${order.originOfficeType}`"
                  :placeholder="`Nama ${destinationOfficeLabel}`"
                  :value="order.destinationOffice.name"
                />
                <template v-if="withStockistCenterBalance">
                  <base-input
                    inset
                    disabled
                    class="col-span-2"
                    label="Saldo Restock"
                    placeholder="Saldo Restock"
                    :value="
                      order.destinationOffice.temp_restock_balance | toCurrency
                    "
                  />
                  <base-input
                    inset
                    disabled
                    class="col-span-2"
                    label="Saldo Nilai Barang Dari Deposit"
                    placeholder="Saldo Nilai Barang Dari Deposit"
                    :value="
                      order.destinationOffice.multiplied_deposit_balance
                        | toCurrency
                    "
                  />
                </template>
                <base-input
                  v-if="withOrderBalance"
                  inset
                  disabled
                  class="col-span-2"
                  label="Saldo Stockist"
                  placeholder="Saldo Stockist"
                  :value="order.destinationOffice.order_balance | toCurrency"
                />
              </div>
            </template>
            <template v-if="withUpline">
              <base-input
                inset
                disabled
                label="No Upline"
                placeholder="No Upline"
                :value="order.destinationOffice.uplineCode"
              />
              <base-input
                inset
                disabled
                label="Nama Upline"
                placeholder="Nama Upline"
                :value="order.destinationOffice.uplineName"
              />
            </template>
            <div v-if="withOriginWarehouse" :class="['col-span-full grid items-start gap-4 sm:grid-cols-2']">
              <template v-if="useOriginOfficeWarehouse">
                <base-input
                  inset
                  disabled
                  id="kode_gudang"
                  label="Kode Gudang"
                  :placeholder="`Gudang ${originOfficeLabel}`"
                  :value="
                    !order.originOffice.id
                      ? ''
                      : `${order.originOffice.currentWarehouse.code} (${order.originOffice.area.code})`
                  "
                />
                <base-input
                  inset
                  disabled
                  label="Nama Gudang"
                  placeholder="Nama Gudang"
                  :value="order.originOffice.currentWarehouse.name"
                />
              </template>
              <template v-else>
                <search-warehouse-input
                  :disabled="
                    saved ||
                    (withReferenceCode && !order.referenceCode) ||
                    !order.date
                  "
                  :warehouse-type="warehouseType"
                  v-model="order.originWarehouse"
                  @change="onChangeOriginWarehouse"
                />
                <base-input
                  inset
                  disabled
                  label="Nama Gudang"
                  placeholder="Nama Gudang"
                  :value="order.originWarehouse.name"
                />
              </template>
              <div v-if="canCustomPrice" class="col-span-full">
                <base-checkbox :disabled="saved" label="Custom Harga" v-model="order.customPrice" @change="onChangeCustomPrice" />
              </div>
              <div :class="visibleBuyerType ? '' : 'col-span-full'">
                <base-input v-if="order.customPrice" inset label="Wilayah Harga" :disabled="saved || ['5', '6', '9'].includes(order.buyerType.code)">
                  <base-select inset expand :options="areaOptions" :disabled="saved || ['5', '6', '9'].includes(order.buyerType.code)" v-model="order.area" />
                </base-input>
                <template v-else>
                  <base-input
                    inset
                    disabled
                    id="wilayah_harga"
                    label="Wilayah Harga"
                    placeholder="Gudang SC1"
                    :value="order.area.code"
                  />
                  <p v-if="order.areaStatus && order.areaStatus !== 'valid'" class="mt-2 text-xs text-gray-500 flex items-center gap-1">
                    <template v-if="order.areaStatus === 'mismatch_address'">
                      <Icon icon="heroicons:exclamation-triangle-solid" />
                      Area tidak sesuai dengan alamat
                    </template>
                    <template v-else>
                      <Icon icon="heroicons:information-circle-solid" />
                      Kebijakan Wilayah
                    </template>
                  </p>
                </template>
              </div>
              <template v-if="visibleBuyerType">
                <base-input v-if="order.customPrice" inset label="Tipe Harga" :disabled="saved">
                  <base-select inset expand :options="buyerTypeOptions" :disabled="saved" v-model="order.buyerType" @change="onChangeBuyerType" />
                </base-input>
                <base-input
                  v-else
                  inset
                  disabled
                  id="tipe_harga"
                  label="Tipe Harga"
                  placeholder="KYN"
                  :value="order.buyerType.code"
                />
              </template>
            </div>
            <template v-if="withOriginStockist">
              <base-input
                inset
                disabled
                label="No Stockist"
                placeholder="No Stockist"
                :value="originStockist ? originStockist.code : ''"
              />
              <base-input
                inset
                disabled
                label="Nama Stockist"
                placeholder="Nama Stockist"
                :value="originStockist ? originStockist.name : ''"
              />
            </template>
          </template>
        </div>
      </base-card>

      <base-card class="space-y-4" v-if="withOptions">
        <div v-if="withRestockType">
          <base-input :label="restockTypeLabel">
            <div class="flex gap-x-6">
              <base-radio
                :disabled="saved"
                :val="true"
                v-model="order.using_stock"
                label="Dengan Barang"
              />
              <base-radio
                :disabled="saved"
                :val="false"
                v-model="order.using_stock"
                label="Tanpa Barang"
              />
            </div>
          </base-input>
        </div>
        <base-input label="Metode Pengiriman">
          <div class="flex gap-x-6">
            <base-radio
              v-if="!forceShipmentType || order.shipment_type === 'pickup'"
              :disabled="saved"
              val="pickup"
              v-model="order.shipment_type"
              label="Diambil"
              @change="updateArea"
            />
            <base-radio
              v-if="!forceShipmentType || order.shipment_type === 'delivery'"
              :disabled="saved"
              val="delivery"
              v-model="order.shipment_type"
              label="Dikirim"
              @change="updateArea"
            />
          </div>
        </base-input>
        <CreateOrderShipmentAddress
          v-if="order.shipment_type === 'delivery' && order.destinationOffice.id"
          :saved="saved"
          :shipment-address="order.shipmentAddress"
          :with-address-receipt="withAddressReceipt"
          :office="order.destinationOffice"
          :custom="order.custom_shipment_address"
          :select-address-visible="visibleSelectAddressModal"
          :create-address-visible="visibleCustomAddressModal"
          :with-action="withOptions"
          @open-select-address="visibleSelectAddressModal = true"
          @close-select-address="visibleSelectAddressModal = false"
          @open-create-address="visibleCustomAddressModal = true"
          @close-create-address="visibleCustomAddressModal = false"
          @restore-address="onResetShipmentAddress"
          @select-address="onSelectAddress"
          @create-address="onChangeShipmentAddress"
        />
      </base-card>
    </div>
  </div>
</template>

<script>
import SearchOfficeInput from '@/components/office/search-office-input.vue';
import SearchWarehouseInput from '@/components/warehouse/search-warehouse-input.vue';
import BaseRadio from '@/components/base/BaseRadio.vue';
import { requestMixin } from '@/mixins/request/request';
import dayjs from 'dayjs';
import { mapActions } from 'vuex';
import BaseCheckbox from '../base/BaseCheckbox.vue';
import CreateOrderShipmentAddress from './create-order-shipment-address.vue';

/*
order: {
  area: {
    id: null,
    code: null
  },
  referenceCode: null,
  date: new Date,
  destinationOffice: {
    id: null,
    code: null,
    searchCode: null,
    originalCode: null,
    name: null,

    ?address: office.attributes.address,

    currentWarehouse: {
      id: null,
      code: null
    },

    area: {
      id: null,
      code: null
    },

    ?restock_balance: null,
    ?temp_restock_balance: null,
    ?multiplied_deposit_balance: null,
  },
  custom_shipment_address: false,
  originOffice: {
    id: null,
    code: null,
    originalCode: null,
    searchCode: null,
    name: null,
    currentWarehouse: {
      id: null,
      code: null,
    },
    area: {
      id: null,
      code: null
    },
  },
  originOfficeType: null,
  destinationOfficeType: null,
  originWarehouse: {
    id: null,
    code: null,
    name: null,
    searchCode: null,
    originalCode: null,
    area: {
      id: null,
      code: null
    }
  },
  shipment_type: 'pickup',
  shipmentAddress: {
    province: {
      id: null,
      name: null
    },
    city: {
      id: null,
      name: null
    },
    district: {
      id: null,
      name: null
    },
    village: {
      id: null,
      name: null
    },
    address: null,
    area: {
      id: null,
      code: null
    },
  }
}
*/
export default {
  mixins: [requestMixin],
  components: {
    SearchOfficeInput,
    SearchWarehouseInput,
    BaseRadio,
    BaseCheckbox,
    CreateOrderShipmentAddress
  },
  props: {
    warehouseType: null,
    disabledDate: {
      type: Boolean,
      default: true,
    },
    originOfficeTypeOptions: {
      type: Array,
      default: () => [
        { key: 'pusat', value: 'pusat', label: 'Pusat' },
        {
          key: 'kantor_pelayanan',
          value: 'kantor_pelayanan',
          label: 'Kantor Pelayanan',
        },
        {
          key: 'stockist_center',
          value: 'stockist_center',
          label: 'Stockist Center',
        },
      ],
    },
    forceDestination: Boolean,
    value: Object,
    withDestinationArea: Boolean,
    withStockistCenterBalance: Boolean,
    withOrderBalance: Boolean,
    withUpline: Boolean,
    withOriginStockist: Boolean,
    valid: Boolean,
    forceShipmentType: Boolean,
    withReferenceCode: Boolean,
    withDestinationOffice: {
      type: Boolean,
      default: true,
    },
    withOriginWarehouse: {
      type: Boolean,
      default: true,
    },
    withOriginOffice: Boolean,
    withOriginOfficeType: Boolean,
    withOptions: {
      type: Boolean,
      default: true,
    },
    withRestockType: {
      type: Boolean,
      default: true,
    },
    restockTypeLabel: {
      type: String,
      default: 'Metode Restock',
    },
    withLoan: {
      type: Boolean,
      default: true,
    },
    withOriginOfficeArea: {
      type: Boolean,
      default: true,
    },
    originOfficeDisabled: Boolean,
    useOriginOfficeWarehouse: Boolean,
    withAddressReceipt: Boolean,
    useCustomAddress: Boolean,
    useBackupUplineAddress: Boolean,
    useDefaultOfficeOrigin: [Boolean, String],
    withCustomPrice: Boolean
  },
  emits: ['input', 'change-destination-office', 'change-origin-office-type'],
  data() {
    return {
      loading: false,
      visibleCustomAddressModal: false,
      visibleSelectAddressModal: false,
      areas: {
        data: []
      },
      buyerTypes: {
        data: []
      }
    };
  },
  computed: {
    dateMismatch() {
      return (
        true || !dayjs(this.order.date).isSame(this.order.createdDate, 'date')
      );
    },
    originStockist() {
      if (!this.order.destinationOffice.uplineHistories) {
        return null
      }

      return this.order.destinationOffice.uplineHistories.find(upline => upline.type === 'promoted_to_sc_by')
    },
    type() {
      return {
        pusat: 'PO',
        stockist: 'Penjualan',
        stockist_center: 'Restock',
      }[this.order.destinationOfficeType ?? 'pusat'];
    },
    order: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    originOfficeLabel() {
      return {
        pusat: 'Pusat',
        kantor_pelayanan: 'Kantor Pelayanan',
        supplier: 'Supplier',
        stockist_center: 'Stockist Center',
      }[this.order.originOfficeType];
    },
    destinationOfficeLabel() {
      return {
        pusat: 'Pusat',
        member: 'Mitra Usaha',
        kantor_pelayanan: 'Kantor Pelayanan',
        supplier: 'Supplier',
        stockist: 'Stockist',
        stockist_center: 'Stockist Center',
      }[this.order.destinationOfficeType];
    },
    saved() {
      return !!this.order.id;
    },
    visibleBuyerType() {
      if (this.order.customPrice) {
        return true
      }

      return this.order.destinationOffice.code && (this.order.destinationOffice.code.startsWith('KYN') || this.order.destinationOffice.code === 'PRSH')
    },
    canCustomPrice() {
      if (!this.withCustomPrice) {
        return false
      }
      
      if (!this.order.destinationOffice.code) {
        return false
      }

      if (this.order.destinationOffice.code.startsWith('KYN')) {
        return false
      }

      return true
    },
    areaOptions() {
      return this.areas.data.map(area => ({
        key: area.id,
        value: {
          id: area.id,
          code: area.attributes.code
        },
        label: area.attributes.code
      }))
    },
    buyerTypeOptions() {
      return this.buyerTypes.data
        .filter(buyerType => +buyerType.attributes.code > 4)
        .map(buyerType => ({
          key: buyerType.id,
          value: {
            id: buyerType.id,
            code: buyerType.attributes.code
          },
          label: buyerType.attributes.code
        }))
    }
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    async checkOrderOrigin(originOfficeId) {
      const [res, err] = await this.request(`/api/v1/offices/${this.order.destinationOffice.id}/origin-order-destinations`, {
        params: {
          'filter[origin_office_id]': originOfficeId
        }
      })

      if (err) {
        return false
      }

      if (!res.meta.has_origin) {
        return true
      }

      return res.data.length > 0
    },
    async getOrderRouteArea() {
      const destinationOffice = this.order.destinationOffice.id

      if (!destinationOffice) {
        return null
      }

      const originOffice = this.useOriginOfficeWarehouse
        ? this.order.originOffice.id
        : this.order.originWarehouse.officeId;

      this.loading = true

      const [res, err] = await this.request(`/api/v1/offices/${originOffice}/destination-order-routes`, {
        params: {
          'filter[shipment_type]': this.order.shipment_type,
          'filter[destination_office_id]': destinationOffice,
          'include': 'area',
          'fields[order-routes]': 'area',
          'fields[areas]': 'code'
        }
      })

      this.loading = false

      if (err) {
        return null
      } else {
        if (res.data.length === 0) {
          return null
        }

        const area = this.getSingleIncluded(res, res.data[0].relationships.area.data.id)

        return {
          id: area.id,
          code: area.attributes.code
        }
      }
    },
    async loadAreas() {
      const [res, err] = await this.request(`/api/v1/areas`, {
        params: {
          sort: 'code'
        }
      })

      if (!err) {
        this.areas = res
      }
    },
    async loadBuyerTypes() {
      const [res, err] = await this.request(`/api/v1/buyer-types`, {
        params: {
          sort: 'code'
        }
      })

      if (!err) {
        this.buyerTypes = res
      }
    },
    async onChangeDate() {
      if (this.saved) {
        this.loading = true;

        await this.request(`/api/v1/orders/${this.order.id}`, {
          method: 'patch',
          data: {
            data: {
              type: 'orders',
              id: this.order.id,
              attributes: {
                orderedAt: dayjs(this.order.date).toISOString(),
              },
            },
          },
        });

        this.loading = false;
      }
    },
    async onChangeReferenceCode() {
      this.order.referenceCodeSaved = false

      if (this.saved) {
        this.loading = true;

        const [, err] = await this.request(`/api/v1/orders/${this.order.id}`, {
          method: 'patch',
          data: {
            data: {
              type: 'orders',
              id: this.order.id,
              attributes: {
                reference_code: this.order.referenceCode,
              },
            },
          },
        });

        if (err) {
          this.createAlert({
            status: 'error',
            data: 'No Penjualan sudah digunakan',
            timeout: 1000,
          });
        } else {
          this.order.referenceCodeSaved = true 
        }

        this.loading = false;
      } else {
        if (this.order.referenceCode) {
          this.loading = true;

          const [, err] = await this.request(
            `/api/v1/orders/-actions/check-reference-code`,
            {
              params: {
                code: this.order.referenceCode,
              },
            }
          );

          if (err) {
            this.createAlert({
              status: 'error',
              data: 'No Penjualan sudah digunakan',
              timeout: 1000,
            });
          } else {
            this.order.referenceCodeSaved = true
          }

          this.loading = false;
        }
      }
    },
    onChangeDestinationOffice(params = {}) {
      const { addresses = [], address = null } = params;

      if (this.order.destinationOfficeType === 'stockist') {
        this.order.discountType = false
        this.updateBuyerType()
      }

      if (this.useCustomAddress && (addresses.length || address)) {
        const uplineAddress = address || params.addresses[0];

        this.order.custom_shipment_address = true;

        if (this.withAddressReceipt) {
          this.order.shipmentAddress.receipt_name = uplineAddress.attributes.name;
          this.order.shipmentAddress.receipt_phone = uplineAddress.attributes.phone;
        }

        this.order.shipmentAddress.province.id = uplineAddress.attributes.province_id;
        this.order.shipmentAddress.province.name =
          uplineAddress.attributes.province_name;

        this.order.shipmentAddress.city.id = uplineAddress.attributes.city_id;
        this.order.shipmentAddress.city.name = uplineAddress.attributes.city_name;

        this.order.shipmentAddress.district.id = uplineAddress.attributes.district_id;
        this.order.shipmentAddress.district.name =
          uplineAddress.attributes.district_name;

        this.order.shipmentAddress.village.id = uplineAddress.attributes.village_id;
        this.order.shipmentAddress.village.name =
          uplineAddress.attributes.village_name;

        this.order.shipmentAddress.uplineAddress = uplineAddress.attributes.detail;
        this.order.shipmentAddress.area.id = `${uplineAddress.attributes.area_uuid}`;
        this.order.shipmentAddress.area.code = uplineAddress.attributes.area_code;

        this.visibleSelectAddressModal = false;

        this.$emit('change-destination-office');
      } else {
        this.order.custom_shipment_address = false;

        if (this.withAddressReceipt) {
          this.order.shipmentAddress.receipt_name = null;
          this.order.shipmentAddress.receipt_phone = null;
        }

        this.order.shipmentAddress.province.id = null;
        this.order.shipmentAddress.province.name =
          null;

        this.order.shipmentAddress.city.id = null;
        this.order.shipmentAddress.city.name = null;

        this.order.shipmentAddress.district.id = null;
        this.order.shipmentAddress.district.name =
          null;

        this.order.shipmentAddress.village.id = null;
        this.order.shipmentAddress.village.name =
          null;

        this.order.shipmentAddress.uplineAddress = null;
        this.order.shipmentAddress.area.id = null;
        this.order.shipmentAddress.area.code = null;

        this.updateArea();

        this.$emit('change-destination-office');
      }
    },
    onChangeOriginOffice() {
      if (this.withOriginOfficeArea) {
        this.updateArea();
      }
    },
    onChangeOriginWarehouse() {
      this.updateArea();
    },
    async onChangeOriginOfficeType() {
      if (!this.useDefaultOfficeOrigin) {
        this.resetOrigiinOffice()
      } else {
        if (typeof this.useDefaultOfficeOrigin === 'boolean') {
          console.error('not implemented')
        } else {
          if (this.order.originOfficeType !== this.useDefaultOfficeOrigin) {
            this.resetOrigiinOffice()
          } else {
            this.loading = true

            const [res, err] = await this.request(`/api/v1/offices`, {
              params: {
                'filter[code]': 'pusat',
                'filter[office_category_id]': 1,
                'include': 'office.current-warehouse,office.area',
                'fields[simple-offices]': 'office',
                'fields[offices]': 'code,name,current-warehouse,area',
                'fields[warehouses]': 'code,name',
                'fields[areas]': 'code'
              }
            })

            if (!err) {
              const [simpleOffice] = res.data

              const office = this.getSingleIncluded(res, simpleOffice.relationships.office.data.id)

              if (this.order.destinationOfficeType === 'stockist' && !await this.checkOrderOrigin(office.id)) {
                this.order.originOfficeType = null
                this.resetOrigiinOffice()
                this.createAlert({
                  status: 'error',
                  data: `Kebijakan tidak bisa order ke ${office.attributes.code}`,
                  timeout: 1000,
                });
              } else {
                const warehouse = this.getSingleIncluded(res, office.relationships['current-warehouse'].data.id)
                const area = this.getSingleIncluded(res, office.relationships.area.data.id)

                this.order.originOffice.id = office.id;
                this.order.originOffice.code = office.attributes.code;
                this.order.originOffice.originalCode = office.attributes.code;
                this.order.originOffice.searchCode = office.attributes.code;
                this.order.originOffice.name = office.attributes.name;

                this.order.originOffice.currentWarehouse.id = warehouse.id;
                this.order.originOffice.currentWarehouse.code = warehouse.attributes.code;
                this.order.originOffice.currentWarehouse.name = warehouse.attributes.name;

                this.order.originOffice.area.id = area.id;
                this.order.originOffice.area.code = area.attributes.code;

                this.updateArea();
              }
            }

            this.loading = false
          }
        }
      }

      this.$emit('change-origin-office-type', this.order.originOfficeType)
    },
    onClickCustomAddress() {
      this.visibleCustomAddressModal = true;
    },
    onChangeShipmentAddress(address) {
      this.order.custom_shipment_address = true;
      this.order.destinationOffice.hasAddress = true

      if (this.withAddressReceipt) {
        this.order.shipmentAddress.receipt_name = address.data.attributes.name;
        this.order.shipmentAddress.receipt_phone =
          address.data.attributes.phone;
      }

      this.order.shipmentAddress.province.id =
        address.data.attributes.province_id;
      this.order.shipmentAddress.province.name =
        address.data.attributes.province_name;

      this.order.shipmentAddress.city.id = address.data.attributes.city_id;
      this.order.shipmentAddress.city.name = address.data.attributes.city_name;

      this.order.shipmentAddress.district.id =
        address.data.attributes.district_id;
      this.order.shipmentAddress.district.name =
        address.data.attributes.district_name;

      this.order.shipmentAddress.village.id =
        address.data.attributes.village_id;
      this.order.shipmentAddress.village.name =
        address.data.attributes.village_name;

      this.order.shipmentAddress.address = address.data.attributes.detail;
      this.order.shipmentAddress.area.id = `${address.data.attributes.area_uuid}`;
      this.order.shipmentAddress.area.code = address.data.attributes.area_code;

      this.visibleSelectAddressModal = false;

      this.updateArea();
    },
    onResetShipmentAddress() {
      this.order.custom_shipment_address = false;

      if (this.withAddressReceipt) {
        this.order.shipmentAddress.receipt_name = null;
        this.order.shipmentAddress.receipt_phone = null;
      }

      this.order.shipmentAddress.province.id = null;
      this.order.shipmentAddress.province.name = null;

      this.order.shipmentAddress.city.id = null;
      this.order.shipmentAddress.city.name = null;

      this.order.shipmentAddress.district.id = null;
      this.order.shipmentAddress.district.name = null;

      this.order.shipmentAddress.village.id = null;
      this.order.shipmentAddress.village.name = null;

      this.order.shipmentAddress.address = null;
      this.order.shipmentAddress.area.id = null;
      this.order.shipmentAddress.area.code = null;

      this.updateArea();
    },
    onSelectAddress(address) {
      this.order.custom_shipment_address = true;

      if (this.withAddressReceipt) {
        this.order.shipmentAddress.receipt_name = address.attributes.name;
        this.order.shipmentAddress.receipt_phone = address.attributes.phone;
      }

      this.order.shipmentAddress.province.id = address.attributes.province_id;
      this.order.shipmentAddress.province.name =
        address.attributes.province_name;

      this.order.shipmentAddress.city.id = address.attributes.city_id;
      this.order.shipmentAddress.city.name = address.attributes.city_name;

      this.order.shipmentAddress.district.id = address.attributes.district_id;
      this.order.shipmentAddress.district.name =
        address.attributes.district_name;

      this.order.shipmentAddress.village.id = address.attributes.village_id;
      this.order.shipmentAddress.village.name = address.attributes.village_name;

      this.order.shipmentAddress.address = address.attributes.detail;
      this.order.shipmentAddress.area.id = `${address.attributes.area_uuid}`;
      this.order.shipmentAddress.area.code = address.attributes.area_code;

      this.visibleSelectAddressModal = false;

      this.updateArea();
    },
    onChangeCustomPrice() {
      if (!this.order.customPrice) {
        this.order.discountType = false

        this.updateArea()
        this.updateBuyerType()
      }
    },
    onChangeBuyerType() {
      if (['5', '6', '9'].includes(this.order.buyerType.code)) {
        const areaJawa = this.areas.data.find(area => area.attributes.code == 0)

        this.order.area.id = areaJawa.id
        this.order.area.code = areaJawa.attributes.code
        this.order.discountType = false
      }
    },
    resetArea() {
      this.order.area.id = null;
      this.order.area.code = null;
    },
    resetOrigiinOffice() {
      this.order.originOffice.id = null;
      this.order.originOffice.code = null;
      this.order.originOffice.originalCode = null;
      this.order.originOffice.searchCode = null;
      this.order.originOffice.name = null;
      this.order.originOffice.currentWarehouse.id = null;
      this.order.originOffice.currentWarehouse.code = null;
      this.order.originOffice.currentWarehouse.name = null;

      this.resetArea();
    },
    async updateArea() {
      if (this.order.destinationOfficeType === 'stockist' || this.order.destinationOfficeType === 'stockist_center') {
        const orderRouteArea = await this.getOrderRouteArea()

        if (orderRouteArea) {
          this.order.area.id = orderRouteArea.id;
          this.order.area.code = orderRouteArea.code;
          this.order.areaStatus = 'route'

          return
        }
      }

      const destinationAreaId =
        this.order.shipmentAddress.area.id ??
        this.order.destinationOffice.area.id;
      const destinationAreaCode =
        this.order.shipmentAddress.area.code ??
        this.order.destinationOffice.area.code;
      const originWarehouse = this.useOriginOfficeWarehouse
        ? this.order.originOffice.area
        : this.order.originWarehouse.area;

      if (
        this.order.shipment_type === 'pickup' ||
        originWarehouse.code > destinationAreaCode
      ) {
        this.order.area.id = originWarehouse.id;
        this.order.area.code = originWarehouse.code;
        this.order.areaStatus = 'valid'
      } else {
        this.order.area.id = destinationAreaId;
        this.order.area.code = destinationAreaCode;
        this.order.areaStatus = 'valid'
      }
    },
    updateBuyerType() {
      if (this.order.destinationOffice.code.startsWith('KYN')) {
        const buyerType = this.buyerTypes.data.find(item => item.attributes.code === '5')

        this.order.buyerType.id = buyerType.id
        this.order.buyerType.code = buyerType.attributes.code
      } else if (this.order.destinationOffice.code === 'PRSH') {
        const buyerType = this.buyerTypes.data.find(item => item.attributes.code === '9')

        this.order.buyerType.id = buyerType.id
        this.order.buyerType.code = buyerType.attributes.code
      } else {
        const buyerType = this.buyerTypes.data.find(item => item.attributes.code === '10')

        this.order.buyerType.id = buyerType.id
        this.order.buyerType.code = buyerType.attributes.code
      }
    }
  },
  created() {
    if (!this.saved && this.order.destinationOfficeType === 'stockist') {
      this.loadAreas()
      this.loadBuyerTypes()
    }
  }
};
</script>
